/**
 * ProjectManager manages loading and saving projects that have a source
 * and channel component, and are indexed on a channel id (or other unique identifier).
 * It accepts a sources and channels store, which handle communication with the relevant
 * apis for loading and saving sources and channels.
 *
 * ProjectManager throttles saves to only occur once every 30 seconds, unless a force
 * save is requested. If save is called within 30 seconds of the previous save,
 * the save is queued and will be executed after the 30 second interval has passed.
 *
 * If a project manager is destroyed, the enqueued save will be cancelled, if it exists.
 */
import {convertProjectTypeToDisplayName} from '@cdo/apps/lab2/utils';
import {NetworkError} from '@cdo/apps/util/HttpClient';
import {
  currentLocation,
  getEnvironment,
  isProductionEnvironment,
} from '@cdo/apps/utils';

import LabMetricsReporter from '../Lab2MetricsReporter';
import Lab2Registry from '../Lab2Registry';
import {ValidationError} from '../responseValidators';
import {Channel, ProjectAndSources, ProjectSources} from '../types';

import {ChannelsStore} from './ChannelsStore';
import {getProjectThumbnailUrl, updateProjectThumbnail} from './filesApi';
import {SourcesStore} from './SourcesStore';

const {reload} = require('@cdo/apps/utils');

export default class ProjectManager {
  private readonly channelId: string;
  private readonly sourcesStore: SourcesStore;
  private readonly channelsStore: ChannelsStore;
  private readonly metricsReporter: LabMetricsReporter;
  private nextSaveTime: number | null = null;
  private readonly saveInterval: number = 30 * 1000; // 30 seconds
  private saveInProgress = false;
  private saveQueued = false;
  private saveSuccessListeners: ((channel: Channel) => void)[] = [];
  private saveNoopListeners: ((channel?: Channel) => void)[] = [];
  private saveFailListeners: ((error: Error) => void)[] = [];
  private saveStartListeners: (() => void)[] = [];
  // The last source we saved or loaded, or undefined if we have not saved a source yet.
  private lastSource: string | undefined;
  // The next source to save, or undefined if we have no source to save.
  private sourcesToSave: ProjectSources | undefined;
  // The last channel we saved or loaded, or undefined if we have not saved a channel yet.
  private lastChannel: Channel | undefined;
  // The next channel to save, or undefined if we have no channel to save.
  private channelToSave: Channel | undefined;
  // Id of the last timeout we set on a save, or undefined if there is no current timeout.
  // When we enqueue a save, we set a timeout to execute the save after the save interval.
  // If we force a save or destroy the ProjectManager, we clear the remaining timeout,
  // if it exists.
  private currentTimeoutId: number | undefined;
  private destroyed = false;
  private reduceChannelUpdates: boolean;
  private initialSaveComplete: boolean;
  private forceReloading: boolean;
  private isShareView: boolean | undefined;
  private thumbnailUrl: string | undefined;
  private thumbnailPngBlob: Blob | undefined;
  private forceNewVersion: boolean = false;
  private isStandaloneProjectLevel: boolean = false;

  constructor({
    sourcesStore,
    channelsStore,
    channelId,
    reduceChannelUpdates,
    isStandaloneProjectLevel,
    isShareView = false,
    metricsReporter = Lab2Registry.getInstance().getMetricsReporter(),
  }: {
    sourcesStore: SourcesStore;
    channelsStore: ChannelsStore;
    channelId: string;
    reduceChannelUpdates: boolean;
    isStandaloneProjectLevel: boolean;
    isShareView?: boolean;
    metricsReporter?: LabMetricsReporter;
  }) {
    this.channelId = channelId;
    this.sourcesStore = sourcesStore;
    this.channelsStore = channelsStore;
    this.reduceChannelUpdates = reduceChannelUpdates;
    this.initialSaveComplete = false;
    this.forceReloading = false;
    this.metricsReporter = metricsReporter;
    this.isShareView = isShareView;
    this.isStandaloneProjectLevel = isStandaloneProjectLevel;
  }

  getChannelId(): string {
    return this.channelId;
  }

  // Load the project from the sources and channels store.
  // If resetSource is true we return undefined for sources, otherwise we load the sources.
  // The lab itself handles undefined sources, generally by using the start sources instead.
  async load(resetSource?: boolean): Promise<ProjectAndSources> {
    if (this.destroyed) {
      this.throwErrorIfDestroyed('load');
    }
    const sources = resetSource ? undefined : await this.loadAndStoreSources();

    let channel: Channel;
    try {
      channel = await this.channelsStore.load(this.channelId);
    } catch (error) {
      throw new Error('Error loading channel', {cause: error});
    }

    this.lastChannel = channel;
    await this.initializeForceNewVersionState();
    const abuseScore = await this.channelsStore.getAbuseScore(channel);
    const sharingDisabled = await this.channelsStore.getSharingDisabled(
      channel
    );
    const isTeacherOfProjectOwner =
      await this.channelsStore.getIsTeacherOfProjectOwner(channel);
    this.setTitleFromChannel(channel);
    return {
      sources,
      channel,
      abuseScore,
      sharingDisabled,
      isTeacherOfProjectOwner,
    };
  }

  // Restore the given version of the project. This will call restore on the sources store
  // and then load and return the updated sources.
  async restoreSources(versionId: string): Promise<ProjectSources | undefined> {
    if (this.destroyed) {
      this.throwErrorIfDestroyed('restore');
    }
    // Flush the enqueued save, if it exists, before restoring.
    await this.flushSave();
    try {
      await this.sourcesStore.restore(this.channelId, versionId);
    } catch (e) {
      throw new Error('Error restoring sources', {cause: e});
    }
    // Now that we've restored to the previous version, loading sources
    // will load the newly-restored version.
    const sources = await this.loadAndStoreSources();
    return sources;
  }

  /**
   * Load the sources for this project. If a versionId is provided, load that version, otherwise
   * load the latest version. The sources are not stored by the Project Manager.
   * @param versionId Optional version id to load. If not provided, the latest version is loaded.
   * @returns sources for the project.
   */
  async loadSources(versionId?: string) {
    let sources: ProjectSources | undefined;
    try {
      sources = await this.sourcesStore.load(this.channelId, versionId);
    } catch (error) {
      // If there was a validation error or sourceResponse is a 404 (not found),
      // we still want to load the channel. In the case of a validation error,
      // we will default to empty sources. Source can return not found if the project
      // is new. If neither of these cases, throw the error.
      if (error instanceof ValidationError) {
        this.metricsReporter.logWarning(
          `Error validating sources (${error.message}). Defaulting to empty sources.`
        );
      } else if (
        error instanceof NetworkError &&
        (error as NetworkError).response.status === 404
      ) {
        // This is expected if the project is new. Default to empty sources.
      } else {
        throw new Error('Error loading sources', {cause: error});
      }
    }
    return sources;
  }

  hasUnsavedChanges(): boolean {
    return this.sourceChanged();
  }

  // Shut down this project manager. All we do here is clear the existing
  // timeout, if it exists.
  destroy(): void {
    this.resetSaveState();
    this.destroyed = true;
  }

  // Save any enqueued unsaved changes, then destroy the project manager.
  async cleanUp() {
    if (this.sourcesToSave) {
      await this.save(this.sourcesToSave, true);
    }
    this.destroy();
  }

  /**
   * Enqueue a save to happen in the next saveInterval, unless a force save is requested.
   * If a save is already enqueued, update this.sourceToSave with the given source.
   * @param sources ProjectSources: the source to save.
   * @param forceSave boolean: if the save should happen immediately
   * @param forceNewVersion boolean: if the save should create a new version
   * @returns a promise that resolves to a Response. If the save is successful, the response
   * will be empty, otherwise it will contain failure information.
   */
  async save(
    sources: ProjectSources,
    forceSave = false,
    forceNewVersion = false,
    skipSourcesChangedCheck = false
  ) {
    if (this.destroyed) {
      // If we have already been destroyed, don't attempt to save.
      this.resetSaveState();
      return this.getNoopResponseAndSendSaveNoopEvent();
    }
    this.sourcesToSave = sources;
    return await this.enqueueSaveOrSave(
      forceSave,
      forceNewVersion,
      skipSourcesChangedCheck
    );
  }

  /**
   * Try to force save with the last sourcesToSave, if it exists.
   * This is used to flush out any remaining enqueued saves.
   * @returns a promise that resolves to a Response. If the save is successful, the response
   * will be empty, otherwise it will contain failure information.
   */
  async flushSave() {
    if (this.destroyed) {
      // If we have already been destroyed, don't attempt to save.
      this.resetSaveState();
      return this.getNoopResponseAndSendSaveNoopEvent();
    }
    return await this.enqueueSaveOrSave(
      /* forceSave */ true,
      /* forceNewVersion */ false
    );
  }

  /**
   * Rename the current project. Default to saving immediately.
   * @param name new name for the project.
   * @param forceSave Whether or not to save immediately. Default to true, because
   * renames are done in response to a user action.
   * @returns a promise that resolves to a Response. If the rename is successful, the response
   * will be empty, otherwise it will contain failure information.
   */
  async rename(name: string, forceSave = true) {
    if (this.destroyed || !this.lastChannel) {
      // If we have already been destroyed or the channel does not exist,
      // don't attempt to rename.
      return this.getNoopResponseAndSendSaveNoopEvent();
    }
    if (!this.channelToSave) {
      this.channelToSave = JSON.parse(
        JSON.stringify(this.lastChannel)
      ) as Channel;
    }
    this.channelToSave.name = name;
    this.setTitleFromChannel(this.channelToSave);
    return await this.enqueueSaveOrSave(forceSave, /* forceNewVersion */ false);
  }

  async updateChannel(channelUpdates: Partial<Channel>, forceSave = false) {
    if (this.destroyed || !this.lastChannel) {
      // If we have already been destroyed or the channel does not exist,
      // don't attempt to update.
      return this.getNoopResponseAndSendSaveNoopEvent();
    }
    if (!this.channelToSave) {
      this.channelToSave = JSON.parse(
        JSON.stringify(this.lastChannel)
      ) as Channel;
    }
    this.channelToSave = {
      ...this.channelToSave,
      ...channelUpdates,
    };
    return await this.enqueueSaveOrSave(forceSave, /* forceNewVersion */ false);
  }

  /**
   * Returns a share URL for the current project.
   *
   * Share URLs can vary by application environment and project type.  For most
   * project types the share URL is the same as the project edit and view URLs,
   * but has no action appended to the project's channel ID. Weblab is a special
   * case right now, because it shares projects to codeprojects.org.
   *
   * This function depends on the document location to determine the current
   * application environment.
   *
   * @returns {string} Fully-qualified share URL for the current project.
   */
  getShareUrl() {
    if (!this.lastChannel || !this.lastChannel.projectType) {
      return null;
    }
    const location = currentLocation();
    // This will not work for web lab, but we will likely not move web lab to use ProjectManager.
    return (
      location.origin +
      '/projects/' +
      this.lastChannel.projectType +
      '/' +
      this.channelId
    );
  }

  getProjectType() {
    return this.lastChannel?.projectType;
  }

  redirectToRemix() {
    this.throwErrorIfDestroyed('redirectToRemix');
    if (!this.lastChannel || !this.lastChannel.projectType) {
      this.logAndThrowError('Cannot remix without channel');
      return;
    }
    this.channelsStore.redirectToRemix(this.lastChannel);
  }

  redirectToView() {
    this.throwErrorIfDestroyed('redirectToView');
    if (!this.lastChannel || !this.lastChannel.projectType) {
      this.logAndThrowError('Cannot view without channel');
      return;
    }
    this.channelsStore.redirectToView(this.lastChannel);
  }

  /**
   * Publish the current channel.
   */
  publish() {
    this.publishHelper(true);
  }

  /**
   * Unpublish the current channel.
   */
  unpublish() {
    this.publishHelper(false);
  }

  async getVersionList(includeComments: boolean = false) {
    return await this.sourcesStore.getVersionList(
      this.channelId,
      includeComments
    );
  }

  addSaveSuccessListener(listener: (channel: Channel) => void) {
    this.saveSuccessListeners.push(listener);
  }

  addSaveNoopListener(listener: (channel?: Channel) => void) {
    this.saveNoopListeners.push(listener);
  }

  addSaveFailListener(listener: (error: Error) => void) {
    this.saveFailListeners.push(listener);
  }

  addSaveStartListener(listener: () => void) {
    this.saveStartListeners.push(listener);
  }

  getCurrentVersionId(): string | null {
    return this.sourcesStore.getCurrentVersionId();
  }

  getForceNewVersion(): boolean {
    return this.forceNewVersion;
  }

  setForceNewVersion(value: boolean): void {
    this.forceNewVersion = value;
  }

  /**
   * Initialize the forceNewVersion value by checking if the current version has a comment.
   * If the current version has a comment, we set forceNewVersion to true.
   * This is used to prevent overwriting a version that has a comment.
   * @returns void
   */
  private async initializeForceNewVersionState(): Promise<void> {
    const currentVersionId = this.getCurrentVersionId();
    if (!currentVersionId) {
      this.setForceNewVersion(false);
      return;
    }

    try {
      const versionList = await this.getVersionList(true); // include comments
      const currentVersion = versionList.find(
        v => v.versionId === currentVersionId
      );
      const hasComment = !!currentVersion?.comment?.trim();
      this.setForceNewVersion(hasComment);
    } catch (error) {
      // If we can't fetch version list, assume no comment.
      this.metricsReporter.logWarning(
        `Failed to initialize comment state because we couldn't fetch the version list: ${error}`
      );
      this.setForceNewVersion(false);
    }
  }

  isForceReloading(): boolean {
    return this.forceReloading;
  }

  setLastSource(lastSource: ProjectSources) {
    this.lastSource = JSON.stringify(lastSource);
  }

  getLastSource() {
    return this.lastSource
      ? (JSON.parse(this.lastSource) as ProjectSources)
      : undefined;
  }

  getLastChannel() {
    return this.lastChannel;
  }

  getShouldCaptureThumbnail() {
    return this.channelId && this.lastChannel?.isOwner && !this.isShareView;
  }

  setThumbnail(pngBlob: Blob) {
    this.thumbnailPngBlob = pngBlob;
    this.thumbnailUrl = getProjectThumbnailUrl(this.channelId);
  }

  /**
   * Uploads a thumbnail image to the thumbnail path via the files API.
   */
  async saveThumbnail() {
    if (this.thumbnailUrl && this.thumbnailPngBlob) {
      try {
        updateProjectThumbnail(this.channelId, this.thumbnailPngBlob);
      } catch (e) {
        this.metricsReporter.logWarning('Failed to save thumbnail.');
        return;
      }
    } else {
      return Promise.resolve();
    }
  }

  /**
   * Helper function to save a project, called either after a timeout or directly by save().
   * On a save, we check if there are unsaved changes to the source or channel.
   * If there are none, we can skip the save. If only the channel changed, we can
   * skip saving the source.
   * Only if the source save succeeds do we update the channel, as the
   * channel is metadata about the project and we don't want to save it unless the source
   * save succeeded.
   * @param forceNewVersion boolean: If the save should create a new version.
   * @param skipSourcesChangedCheck boolean: If true, we will save the source even if it has not changed.
   * @returns a Promise<void> that resolves when the save is complete or when the save fails.
   * Listeners are notified of save status throughout the process.
   */
  private async saveHelper(
    forceNewVersion: boolean,
    skipSourcesChangedCheck: boolean
  ): Promise<void> {
    // We can't save without a last channel or last source.
    // We also know we don't need to save if we don't have sources to save
    // or a channel to save.
    // We also cannot save if the user is not the owner of this project.
    if (
      !this.lastChannel ||
      !this.lastChannel.isOwner ||
      !(this.sourcesToSave || this.channelToSave)
    ) {
      this.executeSaveNoopListeners(this.lastChannel);
      return;
    }
    this.resetSaveState();
    this.saveInProgress = true;
    this.nextSaveTime = Date.now() + this.saveInterval;
    this.executeSaveStartListeners();
    const sourceChanged = this.sourceChanged();
    const channelChanged = this.channelChanged();
    // If neither source nor channel has actually changed, no need to save again
    // unless skipSourcesChangedCheck is true.
    if (!sourceChanged && !channelChanged && !skipSourcesChangedCheck) {
      this.saveInProgress = false;
      // We can clear sourcesToSave since they have not changed.
      this.sourcesToSave = undefined;
      this.executeSaveNoopListeners(this.lastChannel);
      return;
    }
    // Only save the source if it has changed or we are skipping the changed check.
    if (this.sourcesToSave && (sourceChanged || skipSourcesChangedCheck)) {
      try {
        await this.sourcesStore.save(
          this.channelId,
          this.sourcesToSave,
          this.lastChannel.projectType,
          forceNewVersion || this.getForceNewVersion()
        );
        if (this.thumbnailPngBlob) {
          await this.saveThumbnail();
          this.thumbnailPngBlob = undefined;
        }
      } catch (error) {
        let errorToReport: Error;
        if (error instanceof Error) {
          errorToReport = error as Error;
        } else {
          errorToReport = new Error('Unknown error occurred');
        }
        this.onSaveFail('Error saving sources', errorToReport, 'sources');
        return;
      }
      this.lastSource = JSON.stringify(this.sourcesToSave);

      // If we created a new version (not replacing existing), then we reset the forceNewVersion to false.
      if (forceNewVersion || this.getForceNewVersion()) {
        this.setForceNewVersion(false);
      }
    }

    // Normally, reduceChannelUpdates is false and we update the channel
    // metadata every time source code is saved. When in emergency mode,
    // reduceChannelUpdates is true for HoC levels and we only update
    // channel metadata on the initial save to reduce write pressure on
    // the database. The main user-visible effect of this is that the
    // project's 'last saved' time shown in the UI may be inaccurate for
    // all projects that were saved while emergency mode was active.

    if (!this.reduceChannelUpdates || !this.initialSaveComplete) {
      // As long as we are not in emergency mode, always save the channel,
      // as either the channel has changed and/or the source changed.
      // Even if only the source changed, we still update the channel to modify the last
      // updated time.
      this.channelToSave ||= this.lastChannel;

      // If the sources contain a labConfig entry, then also save this to the
      // channel, which means that the labConfig entry will also be saved in the
      // Project model in the database, specifically inside the value field JSON.
      if (this.sourcesToSave?.labConfig) {
        this.channelToSave = {
          ...this.channelToSave,
          labConfig: this.sourcesToSave?.labConfig,
        };
      }

      if (this.thumbnailUrl && !this.lastChannel?.thumbnailUrl) {
        this.channelToSave = {
          ...this.channelToSave,
          thumbnailUrl: this.thumbnailUrl,
        };
      }

      let channelResponse;
      try {
        channelResponse = await this.channelsStore.save(this.channelToSave);
      } catch (error) {
        this.onSaveFail('Error saving channel', error as Error, 'channel');
        return;
      }
      const channelSaveResponse = await channelResponse.json();
      this.lastChannel = channelSaveResponse as Channel;
    }

    this.saveInProgress = false;
    this.channelToSave = undefined;
    this.sourcesToSave = undefined;
    this.executeSaveSuccessListeners(this.lastChannel);
    this.initialSaveComplete = true;
    this.metricsReporter.publishMetric('Lab2.ProjectSaveSuccess', 1, 'Count');
  }

  private onSaveFail(errorMessage: string, error: Error, type: string) {
    this.saveInProgress = false;
    this.executeSaveFailListeners(error);
    if (error.message.includes('409') || error.message.includes('401')) {
      // If this is a conflict or the user has somehow become unauthorized,
      // we need to reload the page.
      // We set forceReloading to true so the client can skip
      // showing the user a dialog before reload.
      this.forceReloading = true;
      this.metricsReporter.logWarning(`${error.message}. Reloading page.`);
      const errorType = error.message.includes('409')
        ? 'conflict'
        : 'unauthorized';
      this.metricsReporter.publishMetric(
        'Lab2.ProjectSaveFailureClient',
        1,
        'Count',
        [
          {name: 'SaveType', value: type},
          {name: 'ErrorType', value: errorType},
        ]
      );
      reload();
    } else if (error.message.includes('413')) {
      // Log 413s as warnings. The save fail listener should handle these errors and labs should
      // show a reasonable error message to the user.
      this.metricsReporter.logWarning('Project too large to save');
    } else {
      // Otherwise, we log the error, including the message as details.
      this.metricsReporter.logError(errorMessage, error, {
        message: error.message,
      });
      this.metricsReporter.publishMetric(
        'Lab2.ProjectSaveFailure',
        1,
        'Count',
        [{name: 'SaveType', value: type}]
      );
    }
  }

  private canSave(forceSave: boolean): boolean {
    if (this.saveInProgress) {
      return false;
    } else if (forceSave) {
      // If this is a force save, don't wait until the next save time.
      return true;
    } else if (this.nextSaveTime) {
      return this.nextSaveTime <= Date.now();
    }

    return true;
  }

  // Check if we can save immediately. If a save is in progress, we must wait. Otherwise,
  // if forceSave is true or it has been at least 30 seconds since our last save,
  // initiate a save.
  // If forceNewVersion is true, we will create a new version on save.
  // If skipSourcesChangedCheck is true, we will save the source even if it has not changed.
  // If we cannot save now, enqueue a save if one has not already been enqueued and
  // return a noop response.
  private async enqueueSaveOrSave(
    forceSave: boolean,
    forceNewVersion: boolean,
    skipSourcesChangedCheck: boolean = false
  ) {
    if (!this.canSave(forceSave)) {
      if (!this.saveQueued) {
        // enqueue a save
        this.saveQueued = true;
        this.currentTimeoutId = window.setTimeout(
          () => {
            this.saveHelper(forceNewVersion, skipSourcesChangedCheck);
          },
          this.nextSaveTime ? this.nextSaveTime - Date.now() : this.saveInterval
        );
      }
      return this.getNoopResponseAndSendSaveNoopEvent();
    } else {
      // if we can save immediately, initiate a save now. This is an async
      // request.
      return await this.saveHelper(forceNewVersion, skipSourcesChangedCheck);
    }
  }

  private getNoopResponse() {
    return new Response(null, {status: 304});
  }

  private getNoopResponseAndSendSaveNoopEvent() {
    const noopResponse = this.getNoopResponse();
    this.executeSaveNoopListeners(this.lastChannel);
    return noopResponse;
  }

  private sourceChanged(): boolean {
    if (!this.sourcesToSave) {
      return false;
    }
    return this.lastSource !== JSON.stringify(this.sourcesToSave);
  }

  private channelChanged(): boolean {
    // If we don't have a channel to save or a last channel, we can't compare.
    // It isn't possible to have a channelToSave without a lastChannel,
    // as we create channelToSave from lastChannel.
    if (!this.channelToSave || !this.lastChannel) {
      return false;
    }
    return (
      JSON.stringify(this.lastChannel) !== JSON.stringify(this.channelToSave)
    );
  }

  private resetSaveState(): void {
    if (this.currentTimeoutId !== undefined) {
      window.clearTimeout(this.currentTimeoutId);
      this.currentTimeoutId = undefined;
    }
    this.saveQueued = false;
  }

  private throwErrorIfDestroyed(actionName: string) {
    if (this.destroyed) {
      this.logAndThrowError(
        `Tried to ${actionName}, but the Project Manager was destroyed.`
      );
    }
  }

  private logAndThrowError(errorMessage: string) {
    const error = new Error(errorMessage);
    this.metricsReporter.logError(errorMessage, error);
    throw error;
  }

  /**
   * Helper for publish and unpublish methods, since they are so similar.
   * Either publishes or unpublishes lastChannel, depending on the publish parameter.
   * If this ProjectManager has been destroyed, or we're missing a channel, this method
   * will throw an error.
   * @param publish true if we should publish, false is we should unpublish
   * @returns a Promise that resolves when the publish/unpublish is complete
   */
  private async publishHelper(publish: boolean) {
    const actionType = publish ? 'publish' : 'unpublish';
    this.throwErrorIfDestroyed(actionType);
    if (!this.lastChannel || !this.lastChannel.projectType) {
      this.logAndThrowError(`Cannot ${actionType} without channel`);
      return;
    }
    if (publish) {
      return this.channelsStore.publish(this.lastChannel);
    } else {
      return this.channelsStore.unpublish(this.lastChannel);
    }
  }

  /**
   * Load the sources for the given version id, or the latest version if no version id is provided.
   * These sources are stored as lastSource, so any future changes to the sources will be compared
   * to these sources.
   * @param versionId Optional version id to load. If not provided, the latest version is loaded.
   * @returns sources for the project.
   */
  private async loadAndStoreSources(versionId?: string) {
    const sources = await this.loadSources(versionId);
    this.lastSource = JSON.stringify(sources);
    return sources;
  }

  /**
   * Set the title of the page based on the channel name. We only do this for standalone project levels.
   * The title format is:
   * {channel.name} - {project type display name} - CodeAI [{environment}]. If we are on production,
   * we omit the environment suffix, and if we don't have a project type display name, we omit that as well.
   * @param channel
   */
  private setTitleFromChannel(channel: Channel) {
    if (channel.name && this.isStandaloneProjectLevel) {
      const currentEnvironment = getEnvironment();
      const environmentSuffix =
        isProductionEnvironment() || !currentEnvironment
          ? ''
          : ` [${currentEnvironment}]`;
      const projectName = convertProjectTypeToDisplayName(channel.projectType);
      const projectString = projectName ? `${projectName} - ` : '';
      document.title = `${channel.name} - ${projectString}CodeAI${environmentSuffix}`;
    }
    // Otherwise, we will use the default document title from the server.
  }

  // LISTENERS
  private executeSaveSuccessListeners(channel: Channel) {
    this.saveSuccessListeners.forEach(listener => listener(channel));
  }

  private executeSaveNoopListeners(channel?: Channel) {
    this.saveNoopListeners.forEach(listener => listener(channel));
  }

  private executeSaveFailListeners(error: Error) {
    this.saveFailListeners.forEach(listener => listener(error));
  }

  private executeSaveStartListeners() {
    this.saveStartListeners.forEach(listener => listener());
  }
}
